import {isString} from "../bootstrap/common/strings";
import type {Nullable} from "../bootstrap/common/types";
import {GLOBAL} from "../common/globals";

const CONSIDERED_CLICKABLE_TAGS = new Set(["a"]);
const IGNORED_CLICKABLE_TAGS = new Set(["header", "main", "footer"]);

export class TrackingContext {

    public constructor(public clickedElement: Element, public eventElement: Nullable<Element> = null) {
    }

    private static elementIsClickedElement(element: Element): boolean {
        return element.isVisible() && TrackingContext.isClickable(element);
    }

    private static elementIsEventElement(element: Element): boolean {
        return element.getAttribute("data-eventelement") !== null;
    }

    public static fromEvent(event: Event): Nullable<TrackingContext> {
        const eventPath: HTMLElement[] = Array.from(event.composedPath())
            .filter(element => element instanceof HTMLElement);

        const clickedElement = eventPath
            .find(elm => this.elementIsClickedElement(elm));

        const eventElement = eventPath
            .find(elm => this.elementIsEventElement(elm));

        if (clickedElement) {
            return new TrackingContext(clickedElement, eventElement);
        } else {
            return null;
        }
    }

    public static makeContentId(event: Event): string {
        const eventPath: Element[] = Array.from(event.composedPath()
            .filter(element => element instanceof Element));
        return eventPath
            .map(elm => elm.id ? elm.id : elm.getAttribute("data-eventelement"))
            .filter(id => !!id)
            .reverse()
            .join(".");
    }

    public getEventLabel(): string {
        const resolveLabel = (context: TrackingContext): Nullable<string> => {
            const label = context.getEventNameByAttribute("data-tracking-label")
                ?? context.getEventNameByAttribute("title")
                ?? context.getEventNameByTextContent()
                ?? context.getEventNameByAttribute("alt")
                ?? context.getEventNameByImgSrc();
            if (label) {
                return label;
            }
            const nextChild = context.getNextChildForEventLabel(context);
            return nextChild ? resolveLabel(new TrackingContext(nextChild)) : null;
        };
        return resolveLabel(this) ?? "";
    }

    private getNextChildForEventLabel(context: TrackingContext): Nullable<Element> {
        if (context.clickedElement.children.length === 1) {
            return context.clickedElement.children.item(0)!;
        }
        return null;
    }

    private getEventNameByTextContent(): Nullable<string> {
        const text = Array.from(this.clickedElement.childNodes)
            .filter(e => e instanceof Text)
            .map(e => e.textContent)
            .join(" ")
            .trim();

        return this.validNonEmptyString(text);
    }

    private getEventNameByImgSrc(): Nullable<string> {
        let imageFileName = this.getEventNameByAttribute("src");
        if (imageFileName) {
            imageFileName = imageFileName.split("/").pop() ?? null;
        }

        return this.validNonEmptyString(imageFileName);
    }

    private getEventNameByAttribute(name: string): Nullable<string> {
        const eventName = this.clickedElement.getAttribute(name);
        return this.validNonEmptyString(eventName);
    }

    private validNonEmptyString(text: any): Nullable<string> {
        if (!isString(text)) {
            return null;
        }
        const trimmed = text.trim();
        if (trimmed.length === 0) {
            return null;
        }
        return trimmed;
    }

    private static isClickable(element: Element): boolean {
        if (element === GLOBAL.bodyElement()) {
            return false;
        }
        if (element === GLOBAL.htmlElement()) {
            return false;
        }
        const elementTag = element.tagName.toLowerCase();
        if (IGNORED_CLICKABLE_TAGS.has(elementTag)) {
            return false;
        }

        return CONSIDERED_CLICKABLE_TAGS.has(elementTag)
            || !!(this as any).onclick
            || (this as any)._events?.click
            || (this as any)._events?.submit
            || false;
    }
}